import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link, useSearchParams, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Sparkles,
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Eye,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios, { API, getSessionToken } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { useFeatures, FeatureGate } from "@/context/FeaturesContext";
import { createSparkleEffect } from "@/utils/sounds";
import Footer from "@/components/layout/Footer";
import ProfileEditor from "@/components/ProfileEditor";
import { DownloadAllEntriesButton } from "@/components/PdfExporter";
import { InsightsSection } from "@/components/MoodInsights";
import { StreakDisplay, BadgesDisplay } from "@/components/StreaksBadges";
import { MorningAffirmation } from "@/components/DailyPromptAffirmation";
import DiaryEditor from "@/components/journal/DiaryEditor";
import EntryCard from "@/components/journal/EntryCard";
import ViewEntryModal from "@/components/journal/ViewEntryModal";
import MotivateMeButton from "@/components/journal/MotivateMeButton";
import SearchBar from "@/components/journal/SearchBar";
import PromoBanner from "@/components/journal/PromoBanner";
import { useTheme } from "@/context/ThemeContext";
import { NotificationBell, NotificationPopup } from "@/components/NotificationSystem";
import UpgradeBanner, { PricingModal } from "@/components/UpgradeBanner";
import PromoBannerCarousel from "@/components/PromoBannerCarousel";
import MultiVideoEmbedSection from "@/components/MultiVideoEmbedSection";
import B2BPromoBannerDisplay from "@/components/b2b/B2BPromoBannerDisplay";
import ReferralWidget from "@/components/journal/ReferralWidget";
import { usePresenceTracking } from '@/hooks/usePresenceTracking';

const Dashboard = () => {
  const { user, logout, setUser } = useAuth();
  const { theme, isGradientMode, updateTheme, resetTheme, lockPreviewTheme, unlockPreviewTheme } = useTheme();
  const { setPreviewFeatures, customMood, isB2BUser: contextIsB2BUser } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId: urlClientId } = useParams(); // Get clientId from URL if on /portal/:clientId/dashboard
  const [searchParams] = useSearchParams();
  const { confirm } = useDialog();
  
  // Track presence while journaling
  usePresenceTracking('journaling', user?.user_id);
  
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entryDates, setEntryDates] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  // Preview mode state
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewClient, setPreviewClient] = useState(null);
  
  // B2B client branding for footer
  const [b2bClientConfig, setB2bClientConfig] = useState(null);
  
  // Dashboard display settings
  const [displaySettings, setDisplaySettings] = useState({
    video_embed: null,
    show_carousel: true,
    show_sidebar_banner: true,
  });
  
  // Check for preview mode
  useEffect(() => {
    const previewClientId = searchParams.get('preview');
    if (previewClientId) {
      setIsPreviewMode(true);
      setLoading(true);
      // Lock theme to prevent userTheme from overriding
      lockPreviewTheme();
      // Fetch client branding for preview
      axios.get(`${API}/client-portal/preview/${previewClientId}`)
        .then(res => {
          setPreviewClient(res.data.client);
          
          // Set preview features in FeaturesContext so FeatureGate components work correctly
          if (res.data.client?.features) {
            setPreviewFeatures(res.data.client.features);
          }
          
          // Map client theme to ThemeContext format and apply
          // Now supports all 8 colors from the B2B branding system
          if (res.data.client?.theme) {
            const clientTheme = res.data.client.theme;
            const mappedTheme = {
              // Core 4 colors
              primary: clientTheme.brand_color || clientTheme.primary_color || '#3B82F6',
              secondary: clientTheme.brand_color || clientTheme.secondary_color || '#3B82F6', // Same as primary for flat mode
              background: clientTheme.background_color || clientTheme.background_primary || '#0F172A',
              background_secondary: clientTheme.card_color || clientTheme.background_secondary || '#1E293B',
              text: clientTheme.text_color || clientTheme.text_primary || '#F8FAFC',
              text_secondary: clientTheme.secondary_text_color || clientTheme.text_secondary || '#94A3B8',
              // Additional 4 colors for depth
              border: clientTheme.border_color || clientTheme.card_color || '#475569',
              accent: clientTheme.accent_color || clientTheme.brand_color || clientTheme.primary_color || '#22D3EE',
              input_background: clientTheme.input_background || clientTheme.card_color || '#1E293B',
              // Logo colors (use brand color for flat mode)
              logo_primary: clientTheme.brand_color || clientTheme.primary_color || '#3B82F6',
              logo_secondary: clientTheme.brand_color || clientTheme.secondary_color || '#3B82F6',
              mode: 'flat', // ALWAYS flat for B2B - no gradients
            };
            updateTheme(mappedTheme);
          }
          // Create sample entries for preview
          const sampleEntries = [
            {
              id: 'preview-1',
              date: new Date().toISOString(),
              mood: 4,
              content: '<p>Today was a wonderful day! I woke up feeling refreshed and ready to tackle my goals. The morning journaling practice has been helping me stay focused and positive.</p>',
              gratitude: ['My supportive family', 'Good health', 'This amazing journaling app'],
              affirmation: 'I am capable of achieving great things today.',
              created_at: new Date().toISOString(),
            },
            {
              id: 'preview-2', 
              date: new Date(Date.now() - 86400000).toISOString(),
              mood: 5,
              content: '<p>Had an incredible breakthrough at work today. The team really came together and we accomplished something special. Feeling grateful for the opportunity.</p>',
              gratitude: ['Career growth', 'Teamwork', 'Morning coffee'],
              affirmation: 'I attract success and abundance into my life.',
              created_at: new Date(Date.now() - 86400000).toISOString(),
            },
            {
              id: 'preview-3',
              date: new Date(Date.now() - 172800000).toISOString(), 
              mood: 3,
              content: '<p>A more challenging day, but I reminded myself that growth happens outside my comfort zone. Taking time to reflect and recharge.</p>',
              gratitude: ['Quiet moments', 'Self-reflection', 'Progress over perfection'],
              affirmation: 'I embrace challenges as opportunities to grow.',
              created_at: new Date(Date.now() - 172800000).toISOString(),
            }
          ];
          setEntries(sampleEntries);
          setEntryDates(sampleEntries.map(e => new Date(e.date)));
        })
        .catch(err => {
          console.error('Failed to load preview:', err);
          toast.error('Failed to load preview');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [searchParams, updateTheme, lockPreviewTheme]);
  
  // Cleanup preview mode when leaving
  useEffect(() => {
    return () => {
      if (isPreviewMode) {
        unlockPreviewTheme();
      }
    };
  }, [isPreviewMode, unlockPreviewTheme]);
  
  // Check if user is viewing B2B portal based on URL, NOT session
  // /dashboard → Main app (UnCloned Journal branding)
  // /portal/{clientId}/dashboard → B2B portal (client branding)
  const isOnB2BPortalUrl = !!urlClientId || location.pathname.includes('/portal/');
  // Use FeaturesContext isB2BUser as the source of truth for B2B detection
  // This ensures B2B users get proper branding even if they navigate to non-portal URLs
  const isB2BUser = isPreviewMode ? true : (contextIsB2BUser || (isOnB2BPortalUrl && !!user?.client_id));
  
  // Apply B2B theme for regular B2B users (not just preview mode)
  // This ensures NotificationPopup, NotificationBell, and other components get B2B colors
  useEffect(() => {
    const applyB2BTheme = async () => {
      // Apply B2B theme if user is detected as B2B (via FeaturesContext or URL)
      // Skip if in preview mode (preview has its own theme logic)
      if (isPreviewMode) return;
      if (!contextIsB2BUser && !isOnB2BPortalUrl) return;
      if (!user?.client_id && !contextIsB2BUser) return;
      
      try {
        const res = await axios.get(`${API}/user/client-config`);
        if (res.data.has_client && res.data.client) {
          // Store full client config for footer and other uses
          setB2bClientConfig(res.data.client);
          
          const clientTheme = res.data.theme || res.data.client.theme;
          if (clientTheme) {
            console.log('B2B Theme from API:', clientTheme);
            console.log('B2B border_color:', clientTheme.border_color);
            
            const mappedTheme = {
              primary: clientTheme.brand_color || clientTheme.primary_color || '#3B82F6',
              secondary: clientTheme.brand_color || clientTheme.secondary_color || '#3B82F6',
              background: clientTheme.background_color || clientTheme.background_primary || '#0F172A',
              background_secondary: clientTheme.card_color || clientTheme.background_secondary || '#1E293B',
              text: clientTheme.text_color || clientTheme.text_primary || '#F8FAFC',
              text_secondary: clientTheme.secondary_text_color || clientTheme.text_secondary || '#94A3B8',
              logo_primary: clientTheme.brand_color || clientTheme.primary_color || '#3B82F6',
              logo_secondary: clientTheme.brand_color || clientTheme.secondary_color || '#3B82F6',
              accent: clientTheme.accent_color || clientTheme.brand_color || clientTheme.primary_color || '#3B82F6',
              border: clientTheme.border_color || '#334155',
              input_background: clientTheme.input_background || clientTheme.card_color || '#1E293B',
              mode: 'flat', // Always flat for B2B
              _isB2B: true, // Flag to prevent theme reset
            };
            console.log('Mapped theme being applied:', mappedTheme);
            updateTheme(mappedTheme);
            
            // Also directly apply border-rgb to DOM to ensure it takes effect
            const borderColor = clientTheme.border_color || '#334155';
            const hexToRgb = (hex) => {
              const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
              return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
            };
            const borderRgb = hexToRgb(borderColor);
            if (borderRgb) {
              console.log('Directly setting --theme-border-rgb to:', borderRgb);
              document.documentElement.style.setProperty('--theme-border-rgb', borderRgb);
              document.documentElement.style.setProperty('--theme-border', borderColor);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load B2B theme:', err);
      }
    };
    
    applyB2BTheme();
  }, [isOnB2BPortalUrl, isPreviewMode, user?.client_id, updateTheme, contextIsB2BUser]);
  
  // Always use company name for branding display - but ONLY if B2B user
  // Priority: b2bClientConfig (freshly fetched) > previewClient > user object
  const clientAppName = isPreviewMode 
    ? (previewClient?.app_name || previewClient?.name || 'Journal')
    : (isB2BUser ? (b2bClientConfig?.app_name || user?.client_app_name || user?.client_name || 'Journal') : null);
  const clientLogo = isPreviewMode 
    ? previewClient?.logo_url 
    : (isB2BUser ? (b2bClientConfig?.logo_url || user?.client_logo_url) : null);
  const clientFeatures = isPreviewMode ? (previewClient?.features || {}) : (isOnB2BPortalUrl ? (b2bClientConfig?.features || user?.client_features || {}) : {});
  
  // Check if "Powered by UnCloned" should be shown for B2B users
  const showPoweredByUncloned = isB2BUser 
    ? (clientFeatures?.show_uncloned_branding !== false) // B2B: only show if explicitly true or not set
    : true; // Non-B2B: always show
  
  // B2B users should NEVER see billing/upgrade prompts - they get full access to whatever their organization licensed
  const showUpgradePrompts = !isB2BUser; // Only non-B2B users can see upgrade prompts
  
  // Get auth token using the utility function
  const token = getSessionToken();

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    // Also update localStorage for persistence
    localStorage.setItem('uncloned_user', JSON.stringify(updatedUser));
  };

  const fetchEntries = useCallback(async (date) => {
    // Skip fetching in preview mode - we use sample data
    if (isPreviewMode) return;
    
    setLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/entries?date=${dateStr}`);
      setEntries(response.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, isPreviewMode]);

  const fetchEntryDates = useCallback(async () => {
    // Skip fetching in preview mode
    if (isPreviewMode) return;
    
    try {
      const response = await axios.get(`${API}/entries/calendar/dates`);
      setEntryDates(response.data);
    } catch (error) {
      // Silently fail - calendar dates are non-critical
      console.debug("Calendar dates fetch failed:", error.message);
    }
  }, [isPreviewMode]);

  // Fetch dashboard display settings
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        const response = await axios.get(`${API}/dashboard-display`);
        if (response.data) {
          // Merge with defaults to ensure required properties exist
          setDisplaySettings(prev => ({
            ...prev,
            ...response.data,
            // Ensure show_carousel defaults to true if not specified
            show_carousel: response.data.show_carousel !== false,
            show_sidebar_banner: response.data.show_sidebar_banner !== false,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch display settings:", error);
      }
    };
    fetchDisplaySettings();
  }, []);

  useEffect(() => {
    // Skip in preview mode - we already loaded sample data
    if (isPreviewMode) return;
    
    // If user passed from AuthCallback, we're authenticated
    if (location.state?.user || user) {
      fetchEntries(selectedDate);
      fetchEntryDates();
    }
  }, [selectedDate, fetchEntries, fetchEntryDates, location.state?.user, user, isPreviewMode]);

  // Check for payment success and refresh user data
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment');
    
    if (paymentStatus === 'success') {
      // Show success message
      toast.success('Payment successful! Welcome to Premium!', {
        duration: 5000,
      });
      
      // Sync subscription from Stripe and refresh user data
      const syncAndRefresh = async () => {
        try {
          // First, sync subscription status from Stripe (in case webhook hasn't processed yet)
          await axios.post(`${API}/stripe/sync-subscription`, {}, { withCredentials: true });
          
          // Then fetch updated user data
          const response = await axios.get(`${API}/auth/me`);
          if (response.data) {
            setUser(response.data);
            localStorage.setItem('uncloned_user', JSON.stringify(response.data));
          }
        } catch (error) {
          console.error('Failed to sync subscription:', error);
          // Still try to refresh user data even if sync fails
          try {
            const response = await axios.get(`${API}/auth/me`);
            if (response.data) {
              setUser(response.data);
              localStorage.setItem('uncloned_user', JSON.stringify(response.data));
            }
          } catch (e) {
            console.error('Failed to refresh user data:', e);
          }
        }
      };
      
      // Small delay to allow Stripe webhook to process first
      setTimeout(syncAndRefresh, 1000);
      
      // Clean up URL
      navigate('/dashboard', { replace: true });
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment cancelled. No charges were made.');
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, navigate, setUser]);

  const handleSave = (savedEntry) => {
    setIsEditing(false);
    setEditingEntry(null);
    // Update selected date to match the saved entry's date (today for new entries)
    if (savedEntry?.date) {
      const entryDate = new Date(savedEntry.date + 'T00:00:00');
      setSelectedDate(entryDate);
      fetchEntries(entryDate);
    } else {
      fetchEntries(selectedDate);
    }
    fetchEntryDates();
    
    // Trigger badge refresh after saving - badges may have been unlocked
    // Small delay to allow backend to process the entry and update badges
    setTimeout(() => {
      console.log('[Dashboard] Triggering badge refresh after save');
      window.dispatchEvent(new CustomEvent('refreshBadges'));
    }, 500);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setIsEditing(true);
  };

  const handleDelete = async (entryId) => {
    const confirmed = await confirm({
      title: 'Delete Entry',
      message: 'Are you sure you want to delete this journal entry? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Keep Entry',
      variant: 'destructive'
    });
    
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/entries/${entryId}`);
      toast.success("Entry deleted");
      fetchEntries(selectedDate);
      fetchEntryDates();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete entry");
    }
  };

  const handleNewEntry = (event) => {
    createSparkleEffect(event);
    setEditingEntry(null);
    setIsEditing(true);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
  const displayUser = location.state?.user || user;
  
  // Get display name - prefer display_name over real name
  const displayName = displayUser?.display_name || displayUser?.name;

  // Handle logo click - cancel editing if active
  const handleLogoClick = (e) => {
    if (isEditing) {
      e.preventDefault();
      setIsEditing(false);
      setEditingEntry(null);
    }
  };

  return (
    <div className="diary-app min-h-screen p-4 lg:p-8" data-testid="diary-dashboard">
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div 
          className="max-w-7xl mx-auto mb-4 px-4 py-3 rounded-xl flex items-center justify-between"
          style={{
            background: `${theme?.primary || '#3B82F6'}15`, // SOLID tint for B2B
            border: `2px dashed ${theme?.primary || '#3B82F6'}60`
          }}
        >
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5" style={{ color: theme?.primary || '#3B82F6' }} />
            <div>
              <p className="font-semibold text-sm text-white">
                Preview Mode — This is what your B2B client's users see
              </p>
              <p className="text-xs text-white/60">
                Sample entries shown. Actions are disabled in preview mode.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.close()}
            style={{ 
              borderColor: theme?.primary || '#3B82F6',
              color: theme?.primary || '#3B82F6'
            }}
            className="hover:opacity-80"
          >
            Close Preview
          </Button>
        </div>
      )}
      
      <header className="max-w-7xl mx-auto mb-4 lg:mb-6 pt-2 sm:pt-4">
        <div className="flex flex-col items-center gap-2 lg:gap-4">
          <div className="flex flex-col lg:flex-row items-center justify-center lg:gap-4 w-full">
            {/* B2B Users: Show client's app name/logo - links to CLIENT PORTAL dashboard */}
            {isB2BUser ? (
              <Link 
                to={isOnB2BPortalUrl ? `/portal/${urlClientId}/dashboard` : '/dashboard'} 
                onClick={handleLogoClick}
                className="flex items-center justify-center w-full cursor-pointer hover:opacity-90 transition-opacity" 
                data-testid="client-app-name-link"
              >
                {clientLogo ? (
                  <div className="flex items-center justify-center py-4 lg:py-8 px-4">
                    <img 
                      src={clientLogo} 
                      alt={clientAppName || 'Logo'} 
                      className="max-h-16 sm:max-h-20 md:max-h-36 lg:max-h-44 xl:max-h-52 w-auto object-contain"
                      style={{ maxWidth: 'min(90vw, 500px)', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))' }}
                    />
                  </div>
                ) : (
                  <h1 
                    className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-center py-4 lg:py-6"
                    style={{
                      color: 'var(--theme-text, #ffffff)',
                      textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                  >
                    {clientAppName || 'Journal'}
                  </h1>
                )}
              </Link>
            ) : (
              <>
                {/* Mobile & Tablet: Logo with gradient mask - Clickable */}
                <Link 
                  to="/dashboard" 
                  onClick={handleLogoClick}
                  className="lg:hidden w-full px-1 cursor-pointer hover:opacity-90 transition-opacity" 
                  data-testid="mobile-logo-link"
                >
                  <div 
                    className={`w-full h-28 sm:h-36 md:h-44 ${isGradientMode ? 'animate-gradient-x' : ''}`}
                    style={{
                      background: isGradientMode 
                        ? `linear-gradient(90deg, var(--theme-logo-primary) 0%, var(--theme-logo-secondary) 25%, var(--theme-logo-primary) 50%, var(--theme-logo-secondary) 75%, var(--theme-logo-primary) 100%)`
                        : 'var(--theme-logo-primary)',
                      backgroundSize: isGradientMode ? '200% 100%' : 'auto',
                      WebkitMaskImage: `url("/new-logo-homepage.png")`,
                      WebkitMaskSize: 'contain',
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskImage: `url("/new-logo-homepage.png")`,
                      maskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                    }}
                  />
                </Link>
                {/* Desktop: Logo with sparkles - Clickable */}
                <Link 
                  to="/dashboard" 
                  onClick={handleLogoClick}
                  className="hidden lg:flex items-center justify-center w-full px-16 cursor-pointer hover:opacity-90 transition-opacity" 
                  data-testid="desktop-logo-link"
                >
                  <Sparkles className="w-10 h-10 xl:w-12 xl:h-12 float-animation flex-shrink-0 mr-1" style={{ color: 'var(--theme-logo-primary)' }} />
                  <div 
                    className={`h-32 xl:h-36 flex-1 ${isGradientMode ? 'animate-gradient-x' : ''}`}
                    style={{
                      background: isGradientMode 
                        ? `linear-gradient(90deg, var(--theme-logo-primary) 0%, var(--theme-logo-secondary) 25%, var(--theme-logo-primary) 50%, var(--theme-logo-secondary) 75%, var(--theme-logo-primary) 100%)`
                        : 'var(--theme-logo-primary)',
                      backgroundSize: isGradientMode ? '200% 100%' : 'auto',
                      WebkitMaskImage: `url("/new-logo-dashboard.webp")`,
                      WebkitMaskSize: 'contain',
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskImage: `url("/new-logo-dashboard.webp")`,
                      maskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                    }}
                  />
                  <Sparkles className="w-10 h-10 xl:w-12 xl:h-12 float-animation flex-shrink-0 ml-1" style={{ color: 'var(--theme-logo-primary)' }} />
                </Link>
              </>
            )}
          </div>
          
          {/* User info & actions - responsive layout */}
          {/* Mobile: stacked (name above, actions below) | Desktop: single row */}
          <div className="flex flex-col lg:flex-row items-center gap-5 lg:gap-6 mt-4 lg:-mt-4 mb-4 lg:mb-6">
            
            {/* Mobile: Name on its own row | Desktop: part of the row */}
            <div className="flex items-center lg:order-last">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-3 hover:bg-[var(--theme-background-secondary)] p-2"
                    style={{ color: 'var(--theme-text)' }}
                    data-testid="user-menu-btn"
                  >
                    {displayUser?.picture ? (
                      <img 
                        src={displayUser.picture.startsWith('/api') 
                          ? `${API.replace('/api', '')}${displayUser.picture}` 
                          : displayUser.picture
                        } 
                        alt={displayUser.name} 
                        className="w-12 h-12 lg:w-14 lg:h-14 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 lg:w-12 lg:h-12" />
                    )}
                    <span className="text-xl sm:text-xl lg:text-lg font-medium">{displayUser?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() => setShowProfileEditor(true)}
                    className="hover:bg-[var(--theme-background-secondary)] cursor-pointer"
                    style={{ color: 'var(--theme-text)' }}
                    data-testid="edit-profile-btn"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={logout}
                    className="hover:bg-[var(--theme-background-secondary)] cursor-pointer"
                    style={{ color: 'var(--theme-text)' }}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Action buttons - Mobile: second row | Desktop: first in row */}
            <div className="flex items-center gap-4 lg:gap-6">
              <Button
                onClick={handleNewEntry}
                className="btn-primary flex items-center gap-2 lg:gap-3 text-lg lg:text-xl px-10 lg:px-14 py-5 lg:py-6"
                style={{ 
                  background: 'var(--theme-button-bg)',
                  color: 'white'
                }}
                data-testid="new-entry-btn"
              >
                <Plus className="w-6 h-6 lg:w-7 lg:h-7" />
                New Entry
              </Button>
              
              {/* Notification Bell */}
              <NotificationBell />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8">
            {/* Morning Affirmation - shows at top */}
            {!isEditing && <MorningAffirmation userId={user?.user_id} />}
            
            {/* Streak and Badges Display - Feature gated */}
            {!isEditing && (
              <FeatureGate feature="streaks">
                <StreakDisplay />
              </FeatureGate>
            )}
            
            {/* Badges Display - Mobile Only (shows after streaks on mobile) - Feature gated */}
            {!isEditing && (
              <FeatureGate feature="badges">
                <div className="lg:hidden mb-6">
                  <BadgesDisplay compact={true} />
                </div>
              </FeatureGate>
            )}
            
            {/* Mood Insights - collapsible - Feature gated */}
            {!isEditing && (
              <FeatureGate feature="mood_tracking">
                <InsightsSection collapsed={true} />
              </FeatureGate>
            )}
            
            {isEditing ? (
              <DiaryEditor
                entry={editingEntry}
                onSave={handleSave}
                onCancel={() => {
                  setIsEditing(false);
                  setEditingEntry(null);
                }}
                userName={displayName}
              />
            ) : (
              <div className="space-y-6">
                {/* Search Bar */}
                <SearchBar onSelectEntry={(entry) => {
                  // Navigate to the entry's date and open it
                  const entryDate = new Date(entry.date + 'T00:00:00');
                  setSelectedDate(entryDate);
                  setViewingEntry(entry);
                }} />
                
                {/* Video Embed Section - Shows multiple videos based on user's tier */}
                <MultiVideoEmbedSection />
                
                {/* Upgrade Banner - Only for non-B2B users */}
                {showUpgradePrompts && <UpgradeBanner onUpgrade={() => setShowPricingModal(true)} />}
                
                {/* Unified Promo Banner Carousel - Only for main app users, NOT B2B */}
                {!isB2BUser && displaySettings.show_carousel && (
                  <PromoBannerCarousel 
                    onAction={async (action, campaign) => {
                      if (action === 'upgrade') {
                        setShowPricingModal(true);
                      } else if (action === 'manage_subscription') {
                        // Open Stripe Customer Portal
                        // Use window.location.href for mobile compatibility (window.open is blocked on async calls)
                        try {
                          const apiBase = API.replace('/api', '');
                          const response = await fetch(`${apiBase}/api/stripe/create-portal`, {
                            method: 'POST',
                            credentials: 'include',
                          });
                          const data = await response.json();
                          if (data.url) {
                            // Use location.href for mobile - works reliably across all browsers
                            window.location.href = data.url;
                          } else if (data.detail) {
                            toast.error(data.detail);
                          } else {
                            toast.error('Unable to open subscription portal');
                          }
                        } catch (error) {
                          console.error('Error opening subscription portal:', error);
                          toast.error('Failed to open subscription portal');
                        }
                      }
                      console.log('Promo action:', action, campaign);
                    }}
                    includeFoundingBadge={true}
                  />
                )}
                
                {/* B2B Promo Banner Display - Only for B2B users */}
                {isB2BUser && <B2BPromoBannerDisplay theme={theme} />}
                
                {/* Date Picker */}
                <div className="glass-card p-4 flex items-center justify-between">
                  <Button
                    onClick={() => navigateDate(-1)}
                    variant="ghost"
                    className="text-[var(--theme-primary)] hover:bg-[var(--theme-background-secondary)]"
                    data-testid="prev-date-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-center" style={{ color: 'var(--theme-text)' }}>
                    <CalendarIcon className="w-5 h-5 hidden sm:block" style={{ color: 'var(--theme-primary)' }} />
                    <span className="font-semibold text-sm sm:text-base">
                      <span className="sm:hidden">{format(selectedDate, "EEE, MMM d, yyyy")}</span>
                      <span className="hidden sm:inline">{format(selectedDate, "EEEE, MMMM d, yyyy")}</span>
                    </span>
                    {isToday && (
                      <span className="date-badge text-xs">Today</span>
                    )}
                  </div>
                  <Button
                    onClick={() => navigateDate(1)}
                    variant="ghost"
                    className="text-[var(--theme-primary)] hover:bg-[var(--theme-background-secondary)]"
                    disabled={isToday}
                    data-testid="next-date-btn"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Export entries section - only show when there are entries - Feature gated */}
                {entries.length > 0 && (
                  <FeatureGate 
                    feature="export_pdf" 
                    mode="prompt" 
                    onUpgrade={() => setShowPricingModal(true)}
                    showLockBadge={true}
                  >
                    <div className="glass-card p-4 flex items-center justify-between">
                      <div className="flex items-center gap-2" style={{ color: 'var(--theme-text-secondary)' }}>
                        <span className="text-sm">📥 {entries.length} {entries.length === 1 ? 'entry' : 'entries'} for this day</span>
                      </div>
                      <DownloadAllEntriesButton 
                        entries={entries} 
                        clientBranding={isB2BUser ? {
                          logo_url: b2bClientConfig?.logo_url || previewClient?.logo_url,
                          brand_color: b2bClientConfig?.theme?.brand_color || previewClient?.theme?.brand_color,
                          accent_color: b2bClientConfig?.theme?.accent_color || previewClient?.theme?.accent_color,
                          app_name: b2bClientConfig?.app_name || previewClient?.app_name
                        } : null}
                        customMood={customMood}
                      />
                    </div>
                  </FeatureGate>
                )}

                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="loading-spinner" />
                  </div>
                ) : entries.length > 0 ? (
                  <div className="space-y-6">
                    {entries.map((entry, index) => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onView={setViewingEntry}
                        index={index}
                        clientBranding={isB2BUser ? {
                          logo_url: b2bClientConfig?.logo_url || previewClient?.logo_url,
                          brand_color: b2bClientConfig?.theme?.brand_color || previewClient?.theme?.brand_color,
                          accent_color: b2bClientConfig?.theme?.accent_color || previewClient?.theme?.accent_color,
                          app_name: b2bClientConfig?.app_name || previewClient?.app_name
                        } : null}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-state glass-card flex flex-col items-center">
                    <Sparkles className="w-16 h-16 mb-4 float-animation" style={{ color: 'var(--theme-primary)' }} />
                    <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
                      No entries for this day
                    </h3>
                    <p className="mb-4" style={{ color: 'var(--theme-text-secondary)' }}>
                      Start your morning by capturing your thoughts and feelings!
                    </p>
                    <Button
                      onClick={handleNewEntry}
                      className="btn-primary"
                      data-testid="empty-new-entry-btn"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Write Today's Entry
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            {/* Badges Display - Desktop Only - Feature gated */}
            <FeatureGate feature="badges">
              <div className="mb-6 hidden lg:block">
                <BadgesDisplay compact={true} />
              </div>
            </FeatureGate>

            {/* Motivate Me Button - above calendar */}
            <div className="glass-card p-6 mb-6 flex justify-center">
              <MotivateMeButton />
            </div>

            {/* Referral Widget - Share & Win */}
            <ReferralWidget userId={user?.user_id} />

            {/* Promo Banner - show for both regular users (admin-managed) and B2B users (client-managed) */}
            {((!isB2BUser && displaySettings.show_sidebar_banner) || isB2BUser) && <PromoBanner />}

            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center justify-center gap-2">
                <CalendarIcon className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
                <span style={{ color: 'var(--theme-text)' }}>Your Journey</span>
              </h3>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md calendar-centered"
                  modifiers={{
                    hasEntry: entryDates.map((d) => new Date(d)),
                  }}
                  modifiersStyles={{
                    hasEntry: {
                      backgroundColor: "rgba(var(--theme-primary-rgb), 0.3)",
                      borderRadius: "50%",
                    },
                  }}
                  data-testid="calendar"
                />
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}>
                <p className="text-xs text-center" style={{ color: 'var(--theme-text-secondary)' }}>
                  <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: 'rgba(var(--theme-primary-rgb), 0.3)' }} />
                  Days with entries
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "var(--theme-background-secondary)",
            border: "1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.5)",
            color: "var(--theme-text)",
          },
        }}
      />
      
      {/* View Entry Modal */}
      {viewingEntry && (
        <ViewEntryModal 
          entry={viewingEntry} 
          onClose={() => setViewingEntry(null)}
          onEdit={handleEdit}
          onRefresh={() => fetchEntries(selectedDate)}
          clientBranding={isB2BUser ? {
            logo_url: b2bClientConfig?.logo_url || previewClient?.logo_url,
            brand_color: b2bClientConfig?.theme?.brand_color || previewClient?.theme?.brand_color,
            accent_color: b2bClientConfig?.theme?.accent_color || previewClient?.theme?.accent_color,
            app_name: b2bClientConfig?.app_name || previewClient?.app_name
          } : null}
        />
      )}

      {/* Profile Editor Modal */}
      {showProfileEditor && (
        <ProfileEditor
          user={displayUser}
          onClose={() => setShowProfileEditor(false)}
          onUpdate={handleProfileUpdate}
          clientDefaultTheme={isB2BUser && (b2bClientConfig?.theme || previewClient?.theme) ? {
            primary: b2bClientConfig?.theme?.primary_color || previewClient?.theme?.primary_color || '#E91E63',
            secondary: b2bClientConfig?.theme?.secondary_color || previewClient?.theme?.secondary_color || '#9C27B0',
            accent: b2bClientConfig?.theme?.accent_color || b2bClientConfig?.theme?.secondary_color || previewClient?.theme?.accent_color || '#9C27B0',
            brand_color: b2bClientConfig?.theme?.primary_color || previewClient?.theme?.primary_color || '#E91E63',
            accent_color: b2bClientConfig?.theme?.accent_color || b2bClientConfig?.theme?.secondary_color || previewClient?.theme?.accent_color || '#9C27B0',
            background: b2bClientConfig?.theme?.background_primary || previewClient?.theme?.background_primary || '#0f0a1a',
            background_secondary: b2bClientConfig?.theme?.background_secondary || previewClient?.theme?.background_secondary || '#1a1025',
            text: b2bClientConfig?.theme?.text_primary || previewClient?.theme?.text_primary || '#F8FAFC',
            text_secondary: b2bClientConfig?.theme?.text_secondary || previewClient?.theme?.text_secondary || '#94A3B8',
            border: b2bClientConfig?.theme?.border_color || previewClient?.theme?.border_color || '#2d2640',
            logo_primary: b2bClientConfig?.theme?.primary_color || previewClient?.theme?.primary_color || '#E91E63',
            logo_secondary: b2bClientConfig?.theme?.secondary_color || previewClient?.theme?.secondary_color || '#9C27B0',
          } : null}
        />
      )}

      {/* Notification Popup Modal */}
      {showNotificationPopup && user && (
        <NotificationPopup 
          onClose={() => setShowNotificationPopup(false)} 
        />
      )}

      {/* Pricing Modal - Only for non-B2B users */}
      {!isB2BUser && (
        <PricingModal 
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          onSelectPlan={(planId) => {
            console.log('Selected plan:', planId);
            // TODO: Integrate with Stripe checkout
            toast.info('Payment integration coming soon!');
            setShowPricingModal(false);
          }}
        />
      )}
      
      <Footer 
        appName={isB2BUser ? (b2bClientConfig?.app_name || previewClient?.app_name || clientAppName) : null}
        companyName={isB2BUser ? (b2bClientConfig?.company_name || previewClient?.company_name || previewClient?.name) : null}
        showPoweredBy={showPoweredByUncloned}
        privacyPolicyUrl={isB2BUser ? (b2bClientConfig?.privacy_policy_url || previewClient?.privacy_policy_url) : null}
        termsUrl={isB2BUser ? (b2bClientConfig?.terms_url || previewClient?.terms_url) : null}
      />
    </div>
  );
};

export default Dashboard;
